/**
 * EngineBatchRunsPanel (P144) -- the reusable trigger + watch surface for
 * the shared engine_batch_runs substrate. Lifted from the P143
 * ObligationEngineView so both the obligation console (framework) and the
 * derive console (twin) share one implementation: per-kind cards (live
 * progress bar when `total` is set, else a spinner; result counts on
 * success; error on failure) + a recent-runs table, polling at 2s while any
 * run is active.
 *
 * Admin-only (gated via useMe). Reuses the P142 Spinner + ProgressBar.
 */
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { TFunction } from "i18next";
import { i18n } from "shared/i18n";

import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { modelsDifferSentence } from "shared/engine/modelSentence";
import {
  isActive,
  useCancelEngineBatchRun,
  useEngineBatchRuns,
  useTriggerEngineBatchRun,
} from "shared/api/hooks/useEngineBatchRuns";
import { ProgressBar } from "shared/components/ProgressBar";
import { Spinner } from "shared/components/Spinner";
import { useToast } from "shared/toast/ToastProvider";
import type { EngineBatchRun } from "shared/api/types";

export interface BatchKind {
  kind: string;
  name: string;
  desc: string;
}

export interface EngineBatchRunsPanelProps {
  testid: string;
  rubric: string;
  heading: string;
  intro: string;
  notAdminMessage: string;
  kinds: BatchKind[];
  /** GET path for this module's runs. */
  listPath: string;
  /** POST path for a given kind. */
  triggerPath: (kind: string) => string;
  /** Show a Module column in the recent-runs table (unified console). */
  showModule?: boolean;
}

const STATUS_PILL: Record<EngineBatchRun["status"], string> = {
  queued: "bg-slate-100 text-slate-700 border-slate-200",
  running: "bg-brand-brass/10 text-brand-brassDark border-brand-brass/30",
  succeeded: "bg-emerald-100 text-emerald-900 border-emerald-200",
  failed: "bg-rose-100 text-rose-900 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

/**
 * D5 -- a run's result, in a line.
 *
 * `${k}: ${v}` on a nested value prints "narratives: [object Object]",
 * which is how the mock exam reported its own output. A driver may
 * return whatever shape suits it, so this summarises the shapes rather
 * than assuming every value is a scalar: a count for a list or an
 * object, the value itself for anything printable.
 */
function resultValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return String(v.length);
  if (typeof v === "object") return String(Object.keys(v).length);
  return String(v);
}

/** Round 16 A26 -- a driver's key, read as words. Catalogued where
 * known; otherwise "refused_questions" becomes "refused questions"
 * rather than printing the stored key. */
function resultLabel(k: string, t?: TFunction): string {
  const fallback = k.replace(/_/g, " ");
  const key = `screens.engineBatchRunsPanel.resultKey.${k}`;
  if (!t || !i18n.exists(key)) return fallback;
  return t(key);
}

/** Round 19 A45 -- keys a kind reports twice. The mock exam's
 *  `failed` is the length of `refused_questions`, and `narratives` the
 *  notes `narrated` already counts: printed beside them, one figure
 *  read as two. */
const DUPLICATE_KEYS: Record<string, readonly string[]> = {
  mock_exam: ["failed", "narratives"],
};

/** Round 19 A45 -- one sentence per figure where the catalogue has one
 *  ("130 summarised", "19 questions the Engine could not answer"),
 *  rather than "failed: 19" under a raw kind. Without a catalogue the
 *  line reads "label: value", as before. */
export function resultSummary(run: EngineBatchRun, t?: TFunction): string {
  if (!run.result) return "";
  const hidden = t ? (DUPLICATE_KEYS[run.kind] ?? []) : [];
  const sentences = Object.entries(run.result)
    .filter(([k]) => !hidden.includes(k))
    .map(([k, v]) => {
      const sentence = `screens.engineBatchRunsPanel.resultSentence.${k}`;
      const value = resultValue(v);
      if (t && /^\d+$/.test(value) && i18n.exists(sentence, { count: Number(value) })) {
        return t(sentence, { count: Number(value) });
      }
      return `${resultLabel(k, t)}: ${value}`;
    });
  return sentences.join(t ? "; " : " · ");
}

/** Round 19 A45 -- a kind and a module in words, not stored keys. */
export function kindLabel(
  kind: string,
  kinds: { kind: string; name: string }[],
  t: TFunction,
): string {
  const known = kinds.find((k) => k.kind === kind);
  if (known) return known.name;
  const key = `screens.engineBatchRunsPanel.kindName.${kind}`;
  return i18n.exists(key) ? t(key) : kind.replace(/_/g, " ");
}

export function moduleLabel(module: string, t: TFunction): string {
  const key = `screens.engineBatchRunsPanel.moduleName.${module}`;
  return i18n.exists(key) ? t(key) : module;
}

function KindCard({
  kind,
  name,
  desc,
  latest,
  onTrigger,
  onCancel,
  pending,
  cancelPending,
}: {
  kind: string;
  name: string;
  desc: string;
  latest: EngineBatchRun | undefined;
  onTrigger: (kind: string) => void;
  onCancel: (runId: string) => void;
  pending: boolean;
  cancelPending: boolean;
}) {
  const { t } = useTranslation();
  const active = latest ? isActive(latest) : false;
  const total = latest?.total ?? 0;
  const processed = latest?.processed ?? 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  // A fast driver (no per-item ticks) reports total=null -> spinner only.
  const showBar = active && total > 0;

  return (
    <section
      data-testid={`engine-card-${kind}`}
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">{name}</h2>
        {latest && (
          <span
            data-testid={`engine-status-${kind}`}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_PILL[latest.status]}`}
          >
            {latest.status}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">{desc}</p>

      {active && latest && (
        <div data-testid={`engine-progress-${kind}`} className="mt-3 space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <Spinner />
            <span className="tabular-nums">
              {showBar
                ? `${processed} / ${total} (${pct}%)`
                : t("screens.engineBatchRunsPanel.running")}
            </span>
            <button
              type="button"
              data-testid={`engine-cancel-${kind}`}
              onClick={() => onCancel(latest.id)}
              disabled={cancelPending}
              className="ms-auto text-[11px] font-medium text-rose-700 hover:underline disabled:opacity-50"
            >
              {t("screens.engineBatchRunsPanel.cancel")}
            </button>
          </div>
          {showBar && (
            <ProgressBar value={processed} max={total} testid={`engine-bar-${kind}`} />
          )}
        </div>
      )}

      {latest && !active && latest.status === "succeeded" && (
        <p
          data-testid={`engine-result-${kind}`}
          className="mt-2 text-[11px] text-slate-500"
        >
          {resultSummary(latest, t)}
        </p>
      )}
      {latest && latest.status === "failed" && (
        <p
          data-testid={`engine-error-${kind}`}
          className="mt-2 text-[11px] text-rose-700"
        >
          {/* Round 16 A26 -- the reason was the server's English; the
              product says what happened and keeps the server's words
              beside it for whoever has to chase them. */}
          {t("screens.engineBatchRunsPanel.runFailed")}
          {latest.error_detail && (
            <details className="mt-0.5 text-slate-500">
              <summary>{t("screens.engineBatchRunsPanel.technicalDetail")}</summary>
              <span dir="ltr">{latest.error_detail}</span>
            </details>
          )}
        </p>
      )}

      <button
        type="button"
        data-testid={`engine-trigger-${kind}`}
        onClick={() => onTrigger(kind)}
        disabled={active || pending}
        className="mt-3 rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {active
          ? t("screens.engineBatchRunsPanel.running")
          : t("screens.engineBatchRunsPanel.runKind", { name })}
      </button>
    </section>
  );
}

export function EngineBatchRunsPanel({
  testid,
  rubric,
  heading,
  intro,
  notAdminMessage,
  kinds,
  listPath,
  triggerPath,
  showModule = false,
}: EngineBatchRunsPanelProps) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const runs = useEngineBatchRuns(listPath, { enabled: isAdmin });
  const trigger = useTriggerEngineBatchRun({ listPath, triggerPath });
  const cancel = useCancelEngineBatchRun({ listPath });
  const toast = useToast();

  const latestByKind = useMemo(() => {
    const map = new Map<string, EngineBatchRun>();
    for (const r of runs.data ?? []) {
      if (!map.has(r.kind)) map.set(r.kind, r); // list is newest-first
    }
    return map;
  }, [runs.data]);

  if (!isAdmin) {
    return (
      <div
        data-testid={`${testid}-not-admin`}
        className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {notAdminMessage}
      </div>
    );
  }

  const onTrigger = (kind: string) => {
    trigger.mutate(kind, {
      onSuccess: () => {
        toast.push({
          tone: "info",
          message: `Queued: ${kind}`,
          detail: "Watch the progress; this runs in the background.",
        });
      },
      onError: (err) => {
        toast.push({
          tone: "error",
          message: `Could not start ${kind}`,
          detail: err.message.includes("503")
            ? "Marsad Engine not configured."
            : err.message,
        });
      },
    });
  };

  const onCancelRun = (runId: string) => {
    cancel.mutate(runId, {
      onSuccess: (run) => {
        toast.push({
          tone: "warning",
          message: `Cancellation requested: ${run.kind}`,
          detail:
            run.status === "cancelled"
              ? "Cancelled."
              : "Will stop at the next checkpoint.",
        });
      },
      onError: (err) => {
        toast.push({ tone: "error", message: "Cancel failed", detail: err.message });
      },
    });
  };

  const history = runs.data ?? [];

  return (
    <section data-testid={testid} className="space-y-5">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {rubric}
        </p>
        <h1 className="font-display text-xl text-slate-900">
          <MarsadIcon
            name="engine"
            size={18}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {heading}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{intro}</p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        {kinds.map((k) => (
          <KindCard
            key={k.kind}
            kind={k.kind}
            name={k.name}
            desc={k.desc}
            latest={latestByKind.get(k.kind)}
            onTrigger={onTrigger}
            onCancel={onCancelRun}
            pending={trigger.isPending}
            cancelPending={cancel.isPending}
          />
        ))}
      </div>

      <div>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.engineBatchRunsPanel.recentRuns")}
        </h2>
        {history.length === 0 ? (
          <p data-testid="engine-history-empty" className="text-sm text-slate-500">
            {t("screens.engineBatchRunsPanel.noRunsYet")}
          </p>
        ) : (
          <table className="w-full text-start text-xs">
            <thead className="text-slate-400">
              <tr>
                {showModule && (
                  <th className="py-1 font-medium">
                    {t("screens.engineBatchRunsPanel.module")}
                  </th>
                )}
                <th className="py-1 font-medium">
                  {t("screens.engineBatchRunsPanel.kind")}
                </th>
                <th className="py-1 font-medium">
                  {t("screens.engineBatchRunsPanel.status")}
                </th>
                <th className="py-1 font-medium">
                  {t("screens.engineBatchRunsPanel.progress")}
                </th>
                <th className="py-1 font-medium">
                  {t("screens.engineBatchRunsPanel.model")}
                </th>
                <th className="py-1 font-medium">
                  {t("screens.engineBatchRunsPanel.result")}
                </th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr
                  key={r.id}
                  data-testid="engine-history-row"
                  className="border-t border-slate-100"
                >
                  {showModule && (
                    <td className="py-1 text-[11px] text-slate-500">
                      {moduleLabel(r.module, t)}
                    </td>
                  )}
                  <td className="py-1 text-[11px] text-slate-600">
                    {kindLabel(r.kind, kinds, t)}
                  </td>
                  <td className="py-1">
                    <span
                      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_PILL[r.status]}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="py-1 tabular-nums text-slate-600">
                    {r.processed}
                    {r.total != null ? ` / ${r.total}` : ""}
                  </td>
                  {/* Round 12 C1 -- this printed one value and headed
                      it "Model", and the value was the deployment's
                      configuration rather than the model that answered.
                      An administrator read three stub runs and one real
                      one where the truth is two and two. */}
                  <td className="py-1 text-slate-500">
                    <span data-testid={`engine-run-model-${r.id}`}>
                      {r.model ?? "—"}
                    </span>
                    {r.model_observed === false && r.model != null && (
                      <span
                        data-testid={`engine-run-model-unobserved-${r.id}`}
                        className="ms-1 text-[10px] text-amber-700"
                        title={t(
                          "screens.engineBatchRunsPanel.modelConfiguredOnlyHint",
                        )}
                      >
                        {" "}
                        {t("screens.engineBatchRunsPanel.modelConfiguredOnly")}
                      </span>
                    )}
                    {r.model_observed === true &&
                      r.configured_model != null &&
                      r.configured_model !== r.model && (
                        <span
                          data-testid={`engine-run-model-disagrees-${r.id}`}
                          className="ms-1 text-[10px] text-amber-700"
                        >
                          {" "}
                          {modelsDifferSentence(t, r.model ?? "", r.configured_model)}
                        </span>
                      )}
                  </td>
                  <td className="py-1 text-slate-500">
                    {r.status === "failed" || r.status === "cancelled" ? (
                      <button
                        type="button"
                        data-testid={`engine-retry-${r.id}`}
                        onClick={() => onTrigger(r.kind)}
                        className="text-brand-brassDark hover:underline"
                      >
                        {t("screens.engineBatchRunsPanel.retry")}
                      </button>
                    ) : (
                      resultSummary(r, t) || "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
